<script setup>
import { computed, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

// border-box: measures each segment's rendered width including its own
// padding, so the sliding chip lines up regardless of the padding used
// (text mode's `px-2` vs icon mode's `px-2.5`).
const replyModeSize = useElementSize(wootEditorReplyMode, undefined, {
  box: 'border-box',
});
const privateModeSize = useElementSize(wootEditorPrivateMode, undefined, {
  box: 'border-box',
});

/**
 * Computed boolean indicating if the editor is in private note mode
 * When isReplyRestricted is true, force switch to private note
 * Otherwise, respect the current mode prop
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => {
  if (props.isReplyRestricted) {
    // Force switch to private note when replies are restricted
    return true;
  }
  // Otherwise respect the current mode
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

/**
 * Computes the width of the sliding background chip in pixels
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;

  return `${widthToUse}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of the reply segment when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  const xTranslate = isPrivate.value ? replyModeSize.width.value : 0;

  return `${xTranslate}px`;
});
</script>

<template>
  <button
    class="flex items-center w-auto h-8 p-1 transition-all border rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0 active:scale-[0.995] active:duration-75"
    :disabled="disabled || isReplyRestricted"
    :class="{
      'cursor-not-allowed': disabled || isReplyRestricted,
    }"
    @click="$emit('toggleMode')"
  >
    <div
      ref="wootEditorReplyMode"
      v-tooltip.top="iconOnly ? $t('CONVERSATION.REPLYBOX.REPLY') : null"
      class="flex items-center gap-1 z-20"
      :class="iconOnly ? 'justify-center px-2.5' : 'px-2'"
    >
      <span v-if="iconOnly" class="i-lucide-message-circle size-4" />
      <template v-else>{{ $t('CONVERSATION.REPLYBOX.REPLY') }}</template>
    </div>
    <div
      ref="wootEditorPrivateMode"
      v-tooltip.top="iconOnly ? $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') : null"
      class="flex items-center gap-1 z-20"
      :class="iconOnly ? 'justify-center px-2.5' : 'px-2'"
    >
      <span v-if="iconOnly" class="i-lucide-lock-keyhole size-4" />
      <template v-else>{{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}</template>
    </div>
    <div
      class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
      :class="{
        'transition-all duration-300': !disabled && !isReplyRestricted,
      }"
      :style="{
        '--chip-width': width,
        '--translate-x': translateValue,
        '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
      }"
    />
  </button>
</template>
